import { CheckCircle, Download, Home } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function formatAmount(amount) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    return "";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(numeric);
}

function getInvoiceUrl(paymentData) {
  if (paymentData?.invoiceUrl) {
    return paymentData.invoiceUrl;
  }

  if (!paymentData?.registrationCode || !API_BASE_URL) {
    return "";
  }

  return `${API_BASE_URL}/payments/invoices/${encodeURIComponent(paymentData.registrationCode)}/download`;
}

function PaymentSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const paymentData = location.state || {
    registrationCode: searchParams.get("registrationCode") || "",
    transactionId: searchParams.get("transactionId") || "",
    amount: searchParams.get("amount") || "",
    formData: {
      studentName: searchParams.get("studentName") || "",
    },
  };
  const formattedAmount = formatAmount(paymentData?.amount);
  const invoiceUrl = getInvoiceUrl(paymentData);

  const handleDownloadInvoice = () => {
    if (!invoiceUrl) {
      return;
    }

    window.open(invoiceUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-sky-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-xl bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
        {invoiceUrl && (
          <button
            type="button"
            onClick={handleDownloadInvoice}
            className="absolute right-5 top-5 inline-flex h-11 w-11 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100"
            aria-label="Download invoice PDF"
            title="Download invoice PDF"
          >
            <Download className="h-5 w-5" />
          </button>
        )}

        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-black text-slate-800 mb-2">Payment Successful</h1>
          <p className="text-slate-600 mb-6">
            Your registration for Unified Council Olympiads 2026-27 has been successfully completed.
          </p>

          <div className="bg-emerald-50 rounded-2xl p-5 mb-6 text-left space-y-2">
            {paymentData?.registrationCode && (
              <div className="flex justify-between gap-4">
                <span className="text-emerald-700">Reference</span>
                <span className="font-semibold text-emerald-900 text-right">{paymentData.registrationCode}</span>
              </div>
            )}
            {paymentData?.transactionId && (
              <div className="flex justify-between gap-4">
                <span className="text-emerald-700">Transaction ID</span>
                <span className="font-semibold text-emerald-900 text-right">{paymentData.transactionId}</span>
              </div>
            )}
            {paymentData?.formData?.studentName && (
              <div className="flex justify-between gap-4">
                <span className="text-emerald-700">Student</span>
                <span className="font-semibold text-emerald-900 text-right">{paymentData.formData.studentName}</span>
              </div>
            )}
            {formattedAmount && (
              <div className="flex justify-between gap-4">
                <span className="text-emerald-700">Amount Paid</span>
                <span className="font-black text-emerald-900 text-right">{formattedAmount}</span>
              </div>
            )}
            {invoiceUrl && (
              <div className="flex justify-between gap-4">
                <span className="text-emerald-700">Invoice</span>
                <button
                  type="button"
                  onClick={handleDownloadInvoice}
                  className="font-semibold text-sky-700 transition-colors hover:text-sky-600"
                >
                  Download PDF
                </button>
              </div>
            )}
          </div>

          <div className="mb-6 rounded-2xl bg-slate-50 p-5 text-left text-sm leading-6 text-slate-700 space-y-3">
            <p>Your registration details will be sent shortly via WhatsApp and SMS.</p>
            <p>
              An email containing your registration confirmation and links to download your e-books (PDF format),
              based on the option(s) selected during registration, will be sent to your registered email address.
            </p>
            <p>If you do not receive the email within some time, please check your Spam, Junk, or Promotions folder.</p>
            <p>Thank you for choosing Unified Council. We wish you great success in your Olympiad journey!</p>
            <p className="font-semibold text-slate-800">Team Unified Council</p>
          </div>

          <Button
            onClick={() => navigate("/")}
            className="w-full rounded-full py-6 bg-sky-500 hover:bg-sky-400"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Home
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccessPage;
